import React, { CSSProperties } from 'react';
import { Link } from 'react-router-dom';

interface ShipsWithListProps {
  shipsWith: string | null | undefined;
}

const listContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

const shipmentLinkStyle: CSSProperties = {
  color: '#179DB5',
  textDecoration: 'none',
  fontFamily: 'Reddit Sans, sans-serif',
  fontSize: '14px',
  lineHeight: '1.0',
};

const noDataTextStyle: CSSProperties = {
  color: '#999999',
  fontFamily: 'Reddit Sans, sans-serif',
  fontSize: '14px',
  lineHeight: '1.0',
};

/**
 * Parses comma-separated shipment references and displays them as clickable links
 *
 * @param shipsWith - Comma-separated shipment references (e.g., "CR-GF25027.01,CR-BR25128.01,CR-TF25033")
 * @returns Array of parsed references with their contract reference (without shipment number)
 */
const parseShipmentReferences = (shipsWith: string): string[] => {
  if (!shipsWith || shipsWith.trim() === '') {
    return [];
  }

  return shipsWith
    .split(',')
    .map(ref => ref.trim())
    .filter(ref => ref.length > 0);
};

/**
 * Extracts the contract reference from a full shipment reference
 * Examples:
 *   "CR-GF25027.01" -> "CR-GF25027"
 *   "CR-TF25033" -> "CR-TF25033"
 *   "GF25027.01" -> "GF25027"
 */
const getContractReference = (shipmentRef: string): string => {
  // Remove shipment number (everything after the first dot)
  const parts = shipmentRef.split('.');
  return parts[0] || shipmentRef;
};

export const ShipsWithList: React.FC<ShipsWithListProps> = ({ shipsWith }) => {
  if (!shipsWith || shipsWith.trim() === '') {
    return <span style={noDataTextStyle}>N/A</span>;
  }

  const references = parseShipmentReferences(shipsWith);

  if (references.length === 0) {
    return <span style={noDataTextStyle}>N/A</span>;
  }

  return (
    <div style={listContainerStyle}>
      {references.map((ref, index) => {
        const contractRef = getContractReference(ref);
        return (
          <Link
            key={`${ref}-${index}`}
            to={`/contracts/${contractRef}`}
            style={shipmentLinkStyle}
            onMouseEnter={(e) => {
              e.currentTarget.style.textDecoration = 'underline';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.textDecoration = 'none';
            }}
          >
            {ref}
          </Link>
        );
      })}
    </div>
  );
};

export default ShipsWithList;
